/**
 * Camera capture + on-device OCR via Tesseract.js WASM.
 * Receipts, paystubs, hand-written notes — never leaves the device.
 */

const TESS_URL = 'https://esm.sh/tesseract.js@5.1.1?bundle';

interface TesseractMod {
  recognize: (
    image: HTMLCanvasElement | ImageBitmap | string,
    lang: string,
    opts?: { logger?: (m: unknown) => void },
  ) => Promise<{ data: { text: string; confidence: number } }>;
}

let modPromise: Promise<TesseractMod> | null = null;

function loadTesseract(): Promise<TesseractMod> {
  if (modPromise === null) {
    modPromise = import(/* @vite-ignore */ TESS_URL) as Promise<TesseractMod>;
  }
  return modPromise;
}

export interface CaptureResult {
  readonly bitmap: ImageBitmap;
  readonly canvas: HTMLCanvasElement;
}

export async function capturePhoto(): Promise<CaptureResult> {
  if (typeof navigator === 'undefined' || typeof navigator.mediaDevices?.getUserMedia !== 'function') {
    throw new Error('getUserMedia unsupported');
  }
  const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 } }, audio: false });
  try {
    const track = stream.getVideoTracks()[0];
    if (track === undefined) throw new Error('no video track');
    const capture = new (window as unknown as { ImageCapture: new (t: MediaStreamTrack) => { takePhoto: () => Promise<Blob>; grabFrame: () => Promise<ImageBitmap> } }).ImageCapture(track);
    const bitmap = await capture.grabFrame();
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (ctx === null) throw new Error('no canvas ctx');
    ctx.drawImage(bitmap, 0, 0);
    return { bitmap, canvas };
  } finally {
    for (const t of stream.getTracks()) t.stop();
  }
}

export async function ocrCanvas(canvas: HTMLCanvasElement, lang: string = 'eng'): Promise<{ text: string; confidence: number }> {
  const t = await loadTesseract();
  const res = await t.recognize(canvas, lang);
  return { text: res.data.text, confidence: res.data.confidence };
}

export async function ocrImageFile(file: File, lang: string = 'eng'): Promise<{ text: string; confidence: number }> {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('no canvas ctx');
  ctx.drawImage(bitmap, 0, 0);
  return ocrCanvas(canvas, lang);
}
